import { Controller } from "@hotwired/stimulus"

// Uploads a specification archive straight from the browser to Spaces. Three steps, and the
// form only ever carries the third:
//
//   1. ask our presign endpoint for a signed POST policy (the server picks the key)
//   2. POST the bytes to Spaces with XHR, which is what gives a progress event
//   3. write Shrine's `{id, storage, metadata}` JSON into the hidden field
//
// The endpoint is Shrine's own, so the response is its standard `{method, url, fields}` and
// the client assembles the attachment hash — the contract Uppy's Shrine integration expects,
// which is what makes swapping this for Uppy a client-only change.
//
// XHR rather than fetch: `fetch` still cannot report upload progress, and a five-minute wait
// with no feedback reads as a hung page. See docs/direct-uploads.md for why any of this.
export default class extends Controller {
  static targets = ["input", "hidden", "progress", "bar", "status"]
  // **Values, not `this.element.dataset`.** The form hosts three controllers
  // (`notification-toggle default-url direct-upload`), so a bare `data-complete-message` sits
  // in a namespace all three share and a fourth could silently claim it. Values namespace
  // themselves (`data-direct-upload-complete-message-value`) and carry defaults, so a renamed
  // or missing one degrades to readable copy rather than rendering "undefined" into the status
  // line — which is the silent failure these messages exist to prevent.
  static values = {
    presignUrl: String,
    cachePrefix: String,
    uploadingMessage: { type: String, default: "Uploading…" },
    completeMessage: { type: String, default: "Upload complete." },
    presignFailedMessage: { type: String, default: "Couldn't start the upload." },
    uploadFailedMessage: { type: String, default: "The upload was rejected." },
    networkFailedMessage: { type: String, default: "The upload was interrupted." },
    abortedMessage: { type: String, default: "Upload cancelled." }
  }

  connect() {
    this.xhr = null
    // Superseded uploads must not touch the form. Every attempt carries this number and
    // compares it before writing anything; see #upload.
    this.attempt = 0
    this.lastPercent = null
  }

  disconnect() {
    // A Turbo navigation mid-upload should not leave the request running against a page
    // that no longer exists.
    this.xhr?.abort()
  }

  // **Picking a second file supersedes the first, and every write below is guarded on that.**
  // Otherwise a small second upload finishes first, then the large first one overwrites its
  // key and Save attaches the file that was explicitly replaced. Aborting alone is not enough:
  // the abort rejects the first attempt, whose handlers would blank the field and re-enable
  // Save under the second — so a superseded attempt is silenced, not merely stopped.
  async upload() {
    const file = this.inputTarget.files[0]
    if (!file) return

    const attempt = ++this.attempt
    this.xhr?.abort()

    this.hiddenTarget.value = ""
    this.#lock()

    try {
      const presign = await this.#presign(file)
      await this.#send(file, presign, attempt)
      if (attempt !== this.attempt) return

      this.hiddenTarget.value = this.#cachedFile(file, presign)
      this.#report(this.completeMessageValue, "text-success")
    } catch (error) {
      if (attempt !== this.attempt) return
      // Leave the hidden field empty: submitting now saves the record with no archive,
      // which is a worse outcome than the member of staff seeing this and retrying.
      this.hiddenTarget.value = ""
      this.#report(error.message, "text-danger")
    } finally {
      if (attempt === this.attempt) this.#unlock()
    }
  }

  async #presign(file) {
    const url = `${this.presignUrlValue}?filename=${encodeURIComponent(file.name)}`
    const response = await fetch(url, { headers: { "Accept": "application/json" } })

    if (!response.ok) throw new Error(this.presignFailedMessageValue)

    return response.json()
  }

  // Shrine's id is the object key *relative to the cache storage's prefix*, while the signed
  // policy carries the absolute key — so the prefix comes off. This is Shrine's own documented
  // handoff; the prefix is passed in rather than hardcoded so the storage can be reconfigured
  // without editing JavaScript.
  #cachedFile(file, presign) {
    const key = presign.fields.key.replace(`${this.cachePrefixValue}/`, "")

    return JSON.stringify({
      id: key,
      storage: "cache",
      metadata: { filename: file.name }
    })
  }

  // Spaces answers a POST policy upload with 204 and no body, so there is nothing to parse
  // — the status is the whole result.
  #send(file, presign, attempt) {
    return new Promise((resolve, reject) => {
      const form = new FormData()
      Object.entries(presign.fields).forEach(([key, value]) => form.append(key, value))
      // The file must be appended last: S3 ignores every field that follows it.
      form.append("file", file)

      const xhr = new XMLHttpRequest()
      this.xhr = xhr

      xhr.upload.addEventListener("progress", (event) => {
        if (attempt !== this.attempt) return
        if (event.lengthComputable) this.#progress(event.loaded / event.total)
      })

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          // A 400 here is usually the policy's own size limit, which is the one refusal a
          // member of staff can act on, so name it rather than reporting a status code.
          reject(new Error(this.uploadFailedMessageValue))
        }
      })

      xhr.addEventListener("error", () => reject(new Error(this.networkFailedMessageValue)))
      xhr.addEventListener("abort", () => reject(new Error(this.abortedMessageValue)))

      xhr.open("POST", presign.url, true)
      xhr.send(form)
    })
  }

  // Progress fires per chunk — thousands of times across a multi-gigabyte upload, for at most
  // 101 distinct values.
  #progress(fraction) {
    const percent = Math.round(fraction * 100)
    if (percent === this.lastPercent) return

    this.lastPercent = percent
    this.barTarget.style.width = `${percent}%`
    this.barTarget.textContent = `${percent}%`
  }

  #report(message, className) {
    this.statusTarget.textContent = message
    this.statusTarget.className = `help-block ${className}`
  }

  // The owning form, via the hidden field — a form control knows its own form, so the
  // controller can sit on the field group instead of having to wrap the submit button.
  get #submits() {
    return this.hiddenTarget.form?.querySelectorAll("input[type=submit], button[type=submit]") ?? []
  }

  // Saving while the bytes are still moving would store a key to an object that does not
  // exist yet, so the submit waits.
  #lock() {
    this.progressTarget.hidden = false
    this.lastPercent = null
    this.#progress(0)
    this.#report(this.uploadingMessageValue, "text-muted")
    this.#submits.forEach((button) => (button.disabled = true))
  }

  #unlock() {
    this.xhr = null

    this.progressTarget.hidden = true
    this.#submits.forEach((button) => (button.disabled = false))
  }
}
